import React, { useState, useEffect } from "react";
import { MapPin, Smartphone, ChevronDown, Loader2 } from "lucide-react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useDarkMode } from "../contexts/DarkModeContext";
import { COUNTRIES_ES } from "../data/countries";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Values must match backend MOBILE_PLATFORM_OPTIONS exactly
const MOBILE_OPTIONS = [
  { value: "iOS", label: "iOS" },
  { value: "Android", label: "Android" },
  { value: "Other", label: "Otro" },
  { value: "None", label: "Ninguno" },
];

const ProfileCompletionModal = () => {
  const { isAuthenticated, userType, token, loading: authLoading } = useAuth();
  const { isDarkMode } = useDarkMode();

  const [needsProfile, setNeedsProfile] = useState(false);
  const [checked, setChecked] = useState(false);
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [mobilePlatform, setMobilePlatform] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Only prompt authenticated regular users (admin exempt)
    if (authLoading) return;
    if (!isAuthenticated || userType !== "user" || !token) {
      setNeedsProfile(false);
      setChecked(false);
      return;
    }
    if (checked) return;

    let cancelled = false;
    const checkProfile = async () => {
      try {
        const res = await axios.get(`${API}/user/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!res.data.profile_completed) {
          setCity(res.data.city || "");
          setState(res.data.state || "");
          setCountry(res.data.country || "");
          setMobilePlatform(res.data.mobile_platform || "");
          setNeedsProfile(true);
        }
      } catch (err) {
        console.error("Error checking profile:", err);
      } finally {
        if (!cancelled) setChecked(true);
      }
    };
    checkProfile();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, userType, token, checked]);

  const isValid = city.trim() !== "" && country.trim() !== "" && mobilePlatform.trim() !== "";

  const handleSave = async () => {
    if (!isValid || saving) return;
    setSaving(true);
    setError("");
    try {
      await axios.post(
        `${API}/user/profile`,
        {
          city: city.trim(),
          state: state.trim(),
          country: country.trim(),
          mobile_platform: mobilePlatform,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Keep stored userInfo in sync
      const stored = localStorage.getItem("userInfo");
      if (stored) {
        try {
          const info = JSON.parse(stored);
          info.profile_completed = true;
          localStorage.setItem("userInfo", JSON.stringify(info));
        } catch (e) {
          /* ignore */
        }
      }

      setNeedsProfile(false);
    } catch (err) {
      console.error("Error saving profile:", err);
      setError(err.response?.data?.detail || "Error al guardar. Intenta nuevamente.");
    } finally {
      setSaving(false);
    }
  };

  if (!needsProfile) return null;

  const inputBase = `w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors duration-200 ${
    isDarkMode
      ? "bg-gray-700 text-white border-gray-600 placeholder-gray-400"
      : "bg-white text-gray-900 border-gray-300 placeholder-gray-400"
  }`;
  const labelBase = `block text-sm font-semibold mb-2 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-testid="profile-completion-overlay"
    >
      <div
        className={`w-full max-w-md rounded-2xl shadow-2xl overflow-hidden ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        }`}
        data-testid="profile-completion-modal"
      >
        {/* Header */}
        <div className="bg-purple-600 px-6 py-5 text-white">
          <div className="flex items-center space-x-3">
            <MapPin size={26} />
            <h2 className="text-xl font-bold">Completa tu información</h2>
          </div>
          <p className="text-sm text-purple-100 mt-2">
            Para continuar, por favor cuéntanos un poco sobre ti.
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-4 max-h-[65vh] overflow-y-auto">
          {/* City (required) */}
          <div>
            <label className={labelBase} htmlFor="profile-city">
              Ciudad <span className="text-red-500">*</span>
            </label>
            <input
              id="profile-city"
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Ingresa tu ciudad"
              className={inputBase}
              data-testid="profile-city-input"
            />
          </div>

          {/* State (optional) */}
          <div>
            <label className={labelBase} htmlFor="profile-state">
              Estado / Provincia <span className="text-gray-400 font-normal">(opcional)</span>
            </label>
            <input
              id="profile-state"
              type="text"
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder="Ingresa tu estado o provincia"
              className={inputBase}
              data-testid="profile-state-input"
            />
          </div>

          {/* Country (required) */}
          <div>
            <label className={labelBase} htmlFor="profile-country">
              País <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                id="profile-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className={`${inputBase} appearance-none cursor-pointer pr-10`}
                data-testid="profile-country-select"
              >
                <option value="">Selecciona un país</option>
                {COUNTRIES_ES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={18}
                className={`absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              />
            </div>
          </div>

          {/* Mobile Platform (required) */}
          <div>
            <label className={labelBase} htmlFor="profile-mobile">
              <span className="inline-flex items-center">
                <Smartphone size={16} className="mr-1.5" />
                Teléfono móvil
              </span>{" "}
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                id="profile-mobile"
                value={mobilePlatform}
                onChange={(e) => setMobilePlatform(e.target.value)}
                className={`${inputBase} appearance-none cursor-pointer pr-10`}
                data-testid="profile-mobile-select"
              >
                <option value="">Selecciona una opción</option>
                {MOBILE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={18}
                className={`absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              />
            </div>
          </div>

          {error && (
            <div
              className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
              data-testid="profile-error"
            >
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 ${isDarkMode ? "bg-gray-800" : "bg-gray-50"}`}>
          <button
            onClick={handleSave}
            disabled={!isValid || saving}
            className="w-full bg-purple-600 text-white font-semibold py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center"
            data-testid="profile-save-button"
          >
            {saving ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              "Guardar"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionModal;
